import { Loader as IDSLoader } from "@ids/react-loader";
import "./index.css";
import {
  type TypographyElement,
  TypographyHeading,
  TypographyHeadingSize,
} from "@ids/react-typography";
import classNames from "classnames";
import type { FC } from "react";
import { Button, ButtonType } from "../button";
import type { ButtonProps } from "../button/types";
import { Divider } from "../divider/Divider";
import {
  type ProductContainerComponent,
  type ProductContainerProps,
  ProductContainerSpacing,
  type SectionProductContainerProps,
  type SectionProps,
  type TopQuestionsContainerProps,
} from "./types";

/**
 * Main container component for product content.
 */
const ProductContainerBase: FC<ProductContainerProps> = ({
  children,
  className,
}) => (
  <div className="product-container">
    <div className={classNames(className, "product-container-inner")}>
      {children}
    </div>
  </div>
);

// Section component
const Section: FC<SectionProductContainerProps> = ({
  children,
  className,
  ref,
  spacing = ProductContainerSpacing.NARROW,
  divider = false,
  showSection = true,
  label,
}) => {
  if (!showSection) {
    return null;
  }
  return (
    <div
      className={classNames(className, "product-container-section", spacing)}
      ref={ref}
    >
      {label && (
        <TypographyHeading as={"h2"} size={TypographyHeadingSize.SMALL}>
          {label.trim()}
        </TypographyHeading>
      )}
      {children}
      {divider && <Divider variant="dark" />}
    </div>
  );
};

// Entry component
const Entry: FC<ProductContainerProps> = ({ children, className, ref }) => (
  <div className={classNames(className, "product-container-entry")} ref={ref}>
    {children}
  </div>
);

// Loader component
const Loader: FC = () => (
  <ProductContainer>
    <Entry>
      <IDSLoader type="dots" size="medium" />
    </Entry>
  </ProductContainer>
);

// Form component
const Form: FC<React.FormHTMLAttributes<HTMLFormElement>> = ({
  className,
  children,
  ...props
}) => (
  <form className={classNames(className, "product-container-form")} {...props}>
    {children}
  </form>
);

/**
 * CTA (Call-To-Action) button component.
 * Accepts all standard button props and renders a styled button.
 */
const CallToAction: FC<ButtonProps> = ({
  className,
  type = ButtonType.FILLED,
  ...props
}) => (
  <div className="product-container-cta">
    <Button
      type={type}
      className={classNames(className, "product-container-cta-button")}
      {...props}
    >
      {props.children}
    </Button>
  </div>
);

/**
 * TopQuestions component for displaying top and bottom question sections.
 */
const TopQuestions: FC<TopQuestionsContainerProps> = ({
  bottomSection,
  topSection,
}) => {
  const renderSection = (
    s: SectionProps,
    as: TypographyElement,
    size: TypographyHeadingSize,
  ) => {
    if (!s.title && !s.component) return null;
    return (
      <Section divider={s.divider}>
        {s.title && (
          <TypographyHeading as={as} size={size}>
            {s.title.trim()}
          </TypographyHeading>
        )}
        {s.component}
      </Section>
    );
  };

  const top = renderSection(topSection, "h1", TypographyHeadingSize.MEDIUM);
  const bottom = renderSection(
    bottomSection,
    "h2",
    TypographyHeadingSize.SMALL,
  );

  if (!top && !bottom) return null;

  return (
    <Section spacing={ProductContainerSpacing.WIDE}>
      {top}
      {bottom}
    </Section>
  );
};

/**
 * Exported ProductContainer component with attached subcomponents.
 */
export const ProductContainer =
  ProductContainerBase as ProductContainerComponent;

ProductContainer.Entry = Entry;
ProductContainer.Form = Form;
ProductContainer.Loader = Loader;
ProductContainer.Section = Section;
ProductContainer.CallToAction = CallToAction;
ProductContainer.TopQuestions = TopQuestions;
